// Temporarily hidden from the cart UI. Set this to true to restore the existing
// split-quantity link and panel; all split functionality below is preserved.
const ENABLE_SPLIT_QUANTITY = false;

class CartOrderSplitter {
  constructor(container) {
    ((this.container = container),
      (this.groups = new Map()),
      (this.allocations = []),
      (this.sources = new Map()),
      (this.virtualLines = []),
      (this.busy = !1),
      (this.renderQueued = !1),
      (this.draggedLineKey = ""),
      (this.lastStatus = null),
      (this.deliveryDateErrors = new Map()),
      (this.minimumDeliveryDate = this.calculateEstimatedDeliveryDate()),
      (this.savedState = this.readSavedState()),
      (this.poMode = this.savedState.poMode === "separate" ? "separate" : "shared"),
      (this.sharedPo = {
        number: String(this.savedState.sharedPo?.number || ""),
        fileUrl: String(this.savedState.sharedPo?.fileUrl || ""),
        fileName: String(this.savedState.sharedPo?.fileName || ""),
        attachments: { ...this.savedState.sharedPo?.attachments },
      }),
      (this.poUploads = new Set()),
      this.collectGroups(),
      this.syncAllocationsFromCart(),
      this.removeEmptyGroups(),
      (this.mode = this.groups.size > 1 ? "multiple" : "single"),
      this.bindEvents(),
      this.render(),
      (this.observer = new MutationObserver((records) => {
        records.some((record) =>
          [...record.addedNodes, ...record.removedNodes].some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.matches?.(
                "[data-cart-order-splitter], [data-order-split-line]",
              ) ||
                node.querySelector?.(
                  "[data-cart-order-splitter], [data-order-split-line]",
                )),
          ),
        ) &&
          (this.setBusy(!1),
          this.syncAllocationsFromCart(),
          this.queueRender());
      })),
      this.observer.observe(this.container, { childList: !0, subtree: !0 }));
  }
  builder() {
    return this.container.querySelector("[data-cart-order-splitter]");
  }
  getLines() {
    return this.virtualLines;
  }
  getSourceLines() {
    return Array.from(
      this.container.querySelectorAll("[data-order-split-line]"),
    );
  }
  findLine(lineKey) {
    return this.getLines().find((line) => line.dataset.lineKey === lineKey);
  }
  storageKey() {
    return `agthia-multi-order:${this.builder()?.dataset.cartToken || "current"}`;
  }
  readSavedState() {
    try {
      return (
        JSON.parse(window.localStorage.getItem(this.storageKey()) || "{}") || {}
      );
    } catch {
      return {};
    }
  }
  persistState() {
    const state = {
      version: 3,
      mode: this.mode,
      poMode: this.poMode,
      sharedPo: { ...this.sharedPo },
      groups: this.sortedGroups().map(([id, group]) => ({
        id,
        name: group.name,
        date: group.date || "",
        po: group.po || "",
        poFileUrl: group.poFileUrl || "",
        poFileName: group.poFileName || "",
        attachments: { ...group.attachments },
      })),
      allocations: this.allocations.map((allocation) => ({
        sourceId: allocation.sourceId,
        groupId: allocation.groupId,
        quantity: allocation.quantity,
      })),
    };
    this.savedState = state;
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify(state));
    } catch {}
  }
  clearSavedState() {
    try {
      window.localStorage.removeItem(this.storageKey());
    } catch {}
  }
  collectGroups() {
    const previous = new Map(this.groups);
    (this.groups.clear(),
      (Array.isArray(this.savedState.groups)
        ? this.savedState.groups
        : []
      ).forEach((group) => {
        group?.id &&
          this.groups.set(String(group.id), {
            name: String(group.name || this.groupName(group.id)),
            date: String(group.date || ""),
            po: String(group.po || ""),
            poFileUrl: String(group.poFileUrl || ""),
            poFileName: String(group.poFileName || ""),
            attachments: { ...group.attachments },
          });
      }));
    const existingOrderOne =
      this.groups.get("order-1") || previous.get("order-1");
    this.groups.set("order-1", {
      name: "Order 1",
      date: existingOrderOne?.date || "",
      po: existingOrderOne?.po || "",
      poFileUrl: existingOrderOne?.poFileUrl || "",
      poFileName: existingOrderOne?.poFileName || "",
      attachments: { ...existingOrderOne?.attachments },
    });
  }
  sourceId(line) {
    const variantId = String(line.dataset.variantId || ""),
      sellingPlanId = String(line.dataset.sellingPlanId || "");
    return variantId
      ? `variant:${variantId}:plan:${sellingPlanId || "none"}`
      : `line:${line.dataset.lineKey || ""}`;
  }
  allocationId(sourceId, groupId) {
    return `${encodeURIComponent(sourceId)}::${groupId}`;
  }
  syncAllocationsFromCart() {
    let pendingTargets = [];
    try {
      const parsed = JSON.parse(
        window.localStorage.getItem("agthia-add-to-order-pending") || "[]",
      );
      Array.isArray(parsed) &&
        (pendingTargets = parsed.filter(
          (target) =>
            target?.sourceId &&
            target?.groupId &&
            Date.now() - Number(target.createdAt || 0) < 1800 * 1e3,
        ));
    } catch {
      pendingTargets = [];
    }
    const nextSources = new Map();
    (this.getSourceLines().forEach((line) => {
      const id = this.sourceId(line),
        data = this.lineData(line),
        existing = nextSources.get(id);
      if (existing) {
        ((existing.quantity += data.quantity),
          (existing.lineTotal += data.lineTotal));
        return;
      }
      nextSources.set(id, {
        id,
        element: line,
        sourceKey: data.key,
        quantity: data.quantity,
        lineTotal: data.lineTotal,
        data,
      });
    }),
      (this.sources = nextSources),
      this.allocations.length === 0 &&
        Array.isArray(this.savedState.allocations) &&
        (this.allocations = this.savedState.allocations
          .map((allocation) => ({
            sourceId: String(allocation.sourceId || ""),
            groupId: String(allocation.groupId || "order-1"),
            quantity: Number(allocation.quantity || 0),
          }))
          .filter(
            (allocation) => allocation.sourceId && allocation.quantity > 0,
          )));
    const normalized = [];
    (nextSources.forEach((source, id) => {
      let allocations = this.allocations.filter(
        (allocation) => allocation.sourceId === id,
      );
      const sourceTargets = pendingTargets.filter(
        (target) => target.sourceId === id,
      );
      if (
        (sourceTargets.forEach((target) => {
          this.groups.has(target.groupId) ||
            this.groups.set(target.groupId, {
              name: String(target.groupName || this.groupName(target.groupId)),
              date: String(target.deliveryDate || ""),
            });
        }),
        allocations.length === 0)
      ) {
        let pendingQuantity = 0;
        if (
          (sourceTargets.forEach((target) => {
            const quantity = Math.min(
              Math.max(0, Math.floor(Number(target.quantity || 0))),
              source.quantity - pendingQuantity,
            );
            quantity > 0 &&
              (allocations.push({
                sourceId: id,
                groupId: target.groupId,
                quantity,
              }),
              (pendingQuantity += quantity));
          }),
          pendingQuantity < source.quantity)
        ) {
          const bootstrapGroupId =
              source.element.dataset.orderGroupId || "order-1",
            bootstrapGroupName =
              source.element.dataset.orderGroupName ||
              this.groupName(bootstrapGroupId);
          (this.groups.has(bootstrapGroupId) ||
            this.groups.set(bootstrapGroupId, {
              name: bootstrapGroupName,
              date: source.element.dataset.orderDeliveryDate || "",
            }),
            allocations.push({
              sourceId: id,
              groupId: bootstrapGroupId,
              quantity: source.quantity - pendingQuantity,
            }));
        }
      }
      allocations = allocations
        .filter(
          (allocation) =>
            this.groups.has(allocation.groupId) && allocation.quantity > 0,
        )
        .map((allocation) => ({
          ...allocation,
          quantity: Math.floor(allocation.quantity),
        }));
      let allocated = allocations.reduce(
        (sum, allocation) => sum + allocation.quantity,
        0,
      );
      if (allocated > source.quantity) {
        let excess = allocated - source.quantity;
        for (
          let index = allocations.length - 1;
          index >= 0 && excess > 0;
          index -= 1
        ) {
          const reduction = Math.min(excess, allocations[index].quantity);
          ((allocations[index].quantity -= reduction), (excess -= reduction));
        }
        ((allocations = allocations.filter(
          (allocation) => allocation.quantity > 0,
        )),
          (allocated = source.quantity));
      }
      if (allocated < source.quantity) {
        let remaining = source.quantity - allocated;
        if (
          (sourceTargets.forEach((target) => {
            if (remaining < 1) return;
            const quantity = Math.min(
              remaining,
              Math.max(0, Math.floor(Number(target.quantity || 0))),
            );
            quantity > 0 &&
              (allocations.push({
                sourceId: id,
                groupId: target.groupId,
                quantity,
              }),
              (remaining -= quantity));
          }),
          remaining > 0)
        ) {
          const orderOne = allocations.find(
            (allocation) => allocation.groupId === "order-1",
          );
          orderOne
            ? (orderOne.quantity += remaining)
            : allocations.push({
                sourceId: id,
                groupId: "order-1",
                quantity: remaining,
              });
        }
      }
      normalized.push(...allocations);
    }),
      (this.allocations = this.mergeAllocations(normalized)),
      this.rebuildVirtualLines(),
      pendingTargets.length &&
        (window.localStorage.removeItem("agthia-add-to-order-pending"),
        window.localStorage.removeItem("agthia-add-to-order-target")));
  }
  mergeAllocations(allocations) {
    const merged = new Map();
    return (
      allocations.forEach((allocation) => {
        const key = `${allocation.sourceId}\0${allocation.groupId}`,
          current = merged.get(key);
        current
          ? (current.quantity += allocation.quantity)
          : merged.set(key, { ...allocation });
      }),
      Array.from(merged.values()).filter(
        (allocation) => allocation.quantity > 0,
      )
    );
  }
  rebuildVirtualLines() {
    this.virtualLines = this.allocations.flatMap((allocation) => {
      const source = this.sources.get(allocation.sourceId);
      if (!source) return [];
      const data = source.data;
      return [
        {
          dataset: {
            lineKey: this.allocationId(allocation.sourceId, allocation.groupId),
            sourceId: allocation.sourceId,
            sourceLineKey: source.sourceKey,
            lineTitle: data.title,
            lineUrl: data.url,
            lineImage: data.image,
            lineSku: data.sku,
            lineUnit: data.unit,
            lineUnitPrice: String(data.unitPrice),
            lineTotal: String(data.unitPrice * allocation.quantity),
            lineInventory: String(data.inventory),
            lineQuantity: String(allocation.quantity),
            variantId: String(data.variantId),
            sellingPlanId: source.element.dataset.sellingPlanId || "",
            orderGroupId: allocation.groupId,
            orderGroupName:
              this.groups.get(allocation.groupId)?.name ||
              this.groupName(allocation.groupId),
          },
        },
      ];
    });
  }
  removeEmptyGroups() {
    const usedGroups = new Set(
      this.getLines().map((line) => line.dataset.orderGroupId || "order-1"),
    );
    Array.from(this.groups.keys()).forEach((id) => {
      id !== "order-1" && !usedGroups.has(id) && this.groups.delete(id);
    });
  }
  groupNumber(id) {
    const match = String(id).match(/(\d+)$/);
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
  }
  groupName(id) {
    const number = this.groupNumber(id);
    return Number.isFinite(number) && number !== Number.MAX_SAFE_INTEGER
      ? `Order ${number}`
      : String(id);
  }
  sortedGroups() {
    return Array.from(this.groups.entries()).sort(
      (a, b) =>
        this.groupNumber(a[0]) - this.groupNumber(b[0]) ||
        a[1].name.localeCompare(b[1].name),
    );
  }
  linesForGroup(groupId) {
    return this.getLines().filter(
      (line) => (line.dataset.orderGroupId || "order-1") === groupId,
    );
  }
  bindEvents() {
    (this.container.addEventListener("submit", (event) => {
      const form = event.target.closest("#cart-form");
      if (!form) return;
      if (this.checkoutSubmitting) return;
      const submitter = event.submitter;
      (submitter && submitter.name !== "checkout") ||
        (event.preventDefault(), this.startCheckout(form));
    }),
      this.container.addEventListener("click", (event) => {
        const actionElement = event.target.closest(
          "[data-order-action], [data-order-split-add], [data-order-summary-scroll]",
        );
        if (!actionElement) return;
        if (actionElement.matches("[data-order-split-add]")) {
          this.addGroup();
          return;
        }
        if (actionElement.matches("[data-order-summary-scroll]")) {
          this.container
            .querySelector("[data-order-preview]")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        const action = actionElement.dataset.orderAction,
          lineKey = actionElement.dataset.lineKey,
          groupId = actionElement.dataset.groupId;
        (action === "remove-po-document" &&
          this.removePoDocument(
            actionElement.dataset.scopeId || "",
            actionElement.dataset.documentKey || "po",
          ),
          action === "remove-group" && this.removeGroup(groupId),
          action === "decrease" && this.changeRenderedQuantity(lineKey, -1),
          action === "increase" && this.changeRenderedQuantity(lineKey, 1),
          action === "remove-line" && this.updateLineQuantity(lineKey, 0),
          action === "split-open" && this.toggleSplitPanel(lineKey, !0),
          action === "split-cancel" && this.toggleSplitPanel(lineKey, !1),
          action === "split-apply" && this.splitRenderedLine(lineKey));
      }),
      this.container.addEventListener("change", (event) => {
        event.target.matches("[data-order-mode]")
          ? this.setMode(event.target.value)
          : event.target.matches("[data-order-quantity-input]")
            ? this.setRenderedQuantity(
                event.target.dataset.lineKey,
                event.target.value,
              )
            : event.target.matches("[data-order-move-select]")
              ? this.chooseDropAction(
                  event.target.dataset.lineKey,
                  event.target.value,
                )
              : event.target.matches("[data-order-delivery-date]")
                ? this.setGroupDate(
                    event.target.dataset.groupId,
                    event.target.value,
                  )
                : event.target.matches("[data-order-po-mode]")
                  ? this.setPoMode(event.target.value)
                  : event.target.matches("[data-order-po]")
                    ? this.setPoNumber(
                        event.target.dataset.scopeId || "",
                        event.target.value,
                      )
                    : event.target.matches("[data-order-po-file]") &&
                      this.uploadPoDocument(
                        event.target.dataset.scopeId || "",
                        event.target.files?.[0],
                        event.target.dataset.documentKey || "po",
                      );
      }),
      this.container.addEventListener("input", (event) => {
        event.target.matches("[data-order-po]") &&
          this.bufferPoNumber(
            event.target.dataset.scopeId || "",
            event.target.value,
          );
      }),
      this.container.addEventListener("keydown", (event) => {
        event.key === "Enter" &&
          event.target.matches("[data-order-quantity-input]") &&
          (event.preventDefault(), event.target.blur());
      }),
      this.container.addEventListener("dragstart", (event) => {
        const row = event.target.closest("[data-rendered-line]");
        !row ||
          event.target.matches("[data-order-quantity-input]") ||
          this.mode !== "multiple" ||
          this.busy ||
          ((this.draggedLineKey = row.dataset.lineKey || ""),
          row.classList.add("is-dragging"),
          (event.dataTransfer.effectAllowed = "move"),
          event.dataTransfer.setData("text/plain", this.draggedLineKey));
      }),
      this.container.addEventListener("dragend", (event) => {
        (event.target
          .closest("[data-rendered-line]")
          ?.classList.remove("is-dragging"),
          this.container
            .querySelectorAll(".is-drag-over")
            .forEach((group) => group.classList.remove("is-drag-over")),
          (this.draggedLineKey = ""));
      }),
      this.container.addEventListener("dragover", (event) => {
        const group = event.target.closest("[data-order-drop-zone]");
        !group ||
          !this.draggedLineKey ||
          this.busy ||
          (event.preventDefault(),
          (event.dataTransfer.dropEffect = "move"),
          group.classList.add("is-drag-over"));
      }),
      this.container.addEventListener("dragleave", (event) => {
        const group = event.target.closest("[data-order-drop-zone]");
        group &&
          !group.contains(event.relatedTarget) &&
          group.classList.remove("is-drag-over");
      }),
      this.container.addEventListener("drop", (event) => {
        const group = event.target.closest("[data-order-drop-zone]");
        !group ||
          !this.draggedLineKey ||
          this.busy ||
          (event.preventDefault(),
          group.classList.remove("is-drag-over"),
          this.chooseDropAction(
            this.draggedLineKey,
            group.dataset.groupId,
          ));
      }));
  }
  queueRender() {
    this.renderQueued ||
      ((this.renderQueued = !0),
      window.requestAnimationFrame(() => {
        ((this.renderQueued = !1), this.rebuildVirtualLines(), this.render());
      }));
  }
  addGroup() {
    if (this.busy) return;
    this.mode = "multiple";
    const highest = this.sortedGroups().reduce(
        (maximum, [id2]) => Math.max(maximum, this.groupNumber(id2) || 0),
        0,
      ),
      next = Number.isFinite(highest) ? highest + 1 : this.groups.size + 1,
      id = `order-${next}`;
    (this.groups.set(id, { name: `Order ${next}`, date: "" }),
      this.persistState(),
      this.render(),
      this.setStatus(
        `${this.groupName(id)} created. Drag products into it or use the Choose order menu.`,
      ));
  }
  removeGroup(groupId) {
    if (!groupId || groupId === "order-1" || this.busy) return;
    const message = this.linesForGroup(groupId).length
      ? `${this.groupName(groupId)} contains products. Move all of them back to Order 1 and remove this order?`
      : `Remove ${this.groupName(groupId)}?`;
    window.confirm(message) &&
      (this.allocations.forEach((allocation) => {
        allocation.groupId === groupId && (allocation.groupId = "order-1");
      }),
      (this.allocations = this.mergeAllocations(this.allocations)),
      this.groups.delete(groupId),
      this.groups.size === 1 && (this.mode = "single"),
      this.rebuildVirtualLines(),
      this.persistState(),
      this.render(),
      this.setStatus(`${this.groupName(groupId)} removed.`));
  }
  setMode(mode) {
    if (
      this.busy ||
      !["single", "multiple"].includes(mode) ||
      mode === this.mode
    )
      return;
    if (mode === "multiple") {
      ((this.mode = "multiple"),
        this.groups.size === 1 && this.addGroup(),
        this.persistState(),
        this.render());
      return;
    }
    if (
      this.getLines().filter(
        (line) => (line.dataset.orderGroupId || "order-1") !== "order-1",
      ).length &&
      !window.confirm(
        "Combine all products into Order 1 and switch to a single order?",
      )
    ) {
      this.renderModeControls();
      return;
    }
    (this.allocations.forEach((allocation) => {
      allocation.groupId = "order-1";
    }),
      (this.allocations = this.mergeAllocations(this.allocations)),
      (this.mode = "single"),
      (this.groups = new Map([
        [
          "order-1",
          this.groups.get("order-1") || { name: "Order 1", date: "" },
        ],
      ])),
      this.rebuildVirtualLines(),
      this.persistState(),
      this.render(),
      this.setStatus("All products are now in one order."));
  }
  render() {
    const workspace = this.container.querySelector(
      "[data-order-split-workspace]",
    );
    if (!workspace) return;
    (this.renderModeControls(),
      this.container.classList.toggle(
        "multi-order-is-single",
        this.mode === "single",
      ),
      this.container.classList.toggle(
        "multi-order-is-multiple",
        this.mode === "multiple",
      ));
    const groups =
      this.mode === "single"
        ? this.sortedGroups().filter(([id]) => id === "order-1")
        : this.sortedGroups();
    ((workspace.innerHTML = groups
      .map(([id, group]) => this.groupMarkup(id, group))
      .join("")),
      this.renderSummary(groups),
      this.renderPoPanel(),
      this.persistState(),
      this.updateCheckoutAvailability(),
      this.lastStatus &&
        this.renderStatus(this.lastStatus.message, this.lastStatus.isError));
  }
  renderModeControls() {
    this.container.querySelectorAll("[data-order-mode]").forEach((radio) => {
      ((radio.checked = radio.value === this.mode),
        (radio.disabled = this.busy));
    });
    const addButton = this.container.querySelector("[data-order-split-add]");
    addButton && (addButton.disabled = this.busy);
  }
  groupMarkup(groupId, group) {
    const lines = this.linesForGroup(groupId),
      removable = groupId !== "order-1",
      minimumDate = this.estimatedDeliveryDate(),
      dateError = this.deliveryDateErrors.get(groupId) || "",
      dateMessageId = `delivery-date-message-${String(groupId).replace(/[^a-zA-Z0-9_-]/g, "-")}`,
      lineMarkup = lines.length
        ? lines.map((line) => this.lineMarkup(line, groupId)).join("")
        : `<div class="multi-order-empty">
          <span class="multi-order-empty__icon" aria-hidden="true">\u21B3</span>
          <strong>Drop products here</strong>
          <span>Drag an item from another order or use its Choose order menu.</span>
        </div>`,
      addProductsUrl = `${this.builder()?.dataset.collectionUrl || "/collections/all"}?agthia_order=${encodeURIComponent(groupId)}&agthia_order_name=${encodeURIComponent(group.name)}&agthia_order_date=${encodeURIComponent(group.date || "")}`;
    return `
      <section class="multi-order-card" data-order-drop-zone data-group-id="${this.escapeHtml(groupId)}">
        <header class="multi-order-card__header">
          <div class="multi-order-card__title">
            <span class="multi-order-drag-dot" aria-hidden="true">\u283F</span>
            <strong>${this.escapeHtml(group.name)}</strong>
            <span>${this.itemCountText(lines)}</span>
            ${
              this.mode === "multiple"
                ? `<span class="multi-order-po-badge">${this.escapeHtml(this.poBadgeText(groupId))}</span>`
                : ""
            }
          </div>
          <label class="multi-order-date${dateError ? " is-invalid" : ""}">
            <span class="multi-order-date__copy">
              <span class="multi-order-date__label">
                Requested Delivery Date
                <span class="multi-order-required" aria-hidden="true">*</span>
              </span>
              <span
                class="multi-order-date__message${dateError ? " multi-order-date__message--error" : ""}"
                id="${dateMessageId}"
              >${this.escapeHtml(dateError || `Available from ${this.formatDate(minimumDate)}`)}</span>
            </span>
            <input
              type="date"
              value="${this.escapeHtml(group.date || "")}"
              min="${minimumDate}"
              ${lines.length ? "required" : ""}
              data-order-delivery-date
              data-group-id="${this.escapeHtml(groupId)}"
              aria-label="Requested delivery date for ${this.escapeHtml(group.name)}"
              aria-required="${lines.length ? "true" : "false"}"
              aria-invalid="${dateError ? "true" : "false"}"
              aria-describedby="${dateMessageId}"
            >
          </label>
          ${
            removable
              ? `<button
            type="button"
            class="multi-order-card__remove"
            data-order-action="remove-group"
            data-group-id="${this.escapeHtml(groupId)}"
            aria-label="Remove ${this.escapeHtml(group.name)}"
          >Remove order</button>`
              : ""
          }
        </header>
        <div class="multi-order-card__lines">${lineMarkup}</div>
        <a class="multi-order-card__add-products" href="${this.escapeHtml(addProductsUrl)}">
          <span aria-hidden="true">+</span> Add more products to this order
        </a>
      </section>`;
  }
  lineMarkup(line, groupId) {
    const data = this.lineData(line),
      groups = this.sortedGroups().filter(([id]) => id !== groupId),
      moveOptions = groups
        .map(
          ([id, group]) =>
            `<option value="${this.escapeHtml(id)}">${this.escapeHtml(group.name)}</option>`,
        )
        .join(""),
      splitTargets = groups
        .map(
          ([id, group]) =>
            `<option value="${this.escapeHtml(id)}">${this.escapeHtml(group.name)}</option>`,
        )
        .join(""),
      canSplit =
        ENABLE_SPLIT_QUANTITY &&
        this.mode === "multiple" &&
        data.quantity > 1 &&
        groups.length > 0,
      stockText =
        data.inventory > 0
          ? `${data.inventory} ${this.escapeHtml(data.unit)}`
          : `Available ${this.escapeHtml(data.unit)}`;
    return `
      <article
        class="multi-order-line"
        draggable="${this.mode === "multiple" ? "true" : "false"}"
        data-rendered-line
        data-line-key="${this.escapeHtml(data.key)}"
      >
        <button type="button" class="multi-order-line__drag" aria-label="Drag ${this.escapeHtml(data.title)} to another order">
          <span aria-hidden="true">\u283F</span>
        </button>
        <div class="multi-order-line__product">
          ${data.image ? `<img src="${this.escapeHtml(data.image)}" alt="" width="64" height="64" loading="lazy">` : '<span class="multi-order-line__placeholder" aria-hidden="true"></span>'}
          <div>
            <a href="${this.escapeHtml(data.url)}">${this.escapeHtml(data.title)}</a>
            <small>SKU: ${this.escapeHtml(data.sku || "\u2014")}</small>
            <small>${this.formatMoney(data.unitPrice)} / ${this.escapeHtml(data.unit)}</small>
          </div>
        </div>
        <div class="multi-order-line__stock">
          <span>Available stock</span>
          <strong>${stockText}</strong>
        </div>
        <div class="multi-order-line__quantity">
          <div class="multi-order-quantity">
            <button type="button" data-order-action="decrease" data-line-key="${this.escapeHtml(data.key)}" aria-label="Decrease quantity">\u2212</button>
            <input
              type="number"
              min="1"
              step="1"
              inputmode="numeric"
              value="${data.quantity}"
              data-order-quantity-input
              data-line-key="${this.escapeHtml(data.key)}"
              aria-label="Quantity for ${this.escapeHtml(data.title)}"
            >
            <button type="button" data-order-action="increase" data-line-key="${this.escapeHtml(data.key)}" aria-label="Increase quantity">+</button>
          </div>
          ${
            canSplit
              ? `<button
            type="button"
            class="multi-order-split-link"
            data-order-action="split-open"
            data-line-key="${this.escapeHtml(data.key)}"
          >Split quantity</button>`
              : ""
          }
        </div>
        <div class="multi-order-line__move">
          ${
            this.mode === "multiple" && groups.length
              ? `<label>
            <span>Choose order</span>
            <select data-order-move-select data-line-key="${this.escapeHtml(data.key)}">
              <option value="">Choose order</option>
              ${moveOptions}
            </select>
          </label>`
              : ""
          }
        </div>
        <strong class="multi-order-line__total">${this.formatMoney(data.lineTotal)}</strong>
        <button
          type="button"
          class="multi-order-line__remove"
          data-order-action="remove-line"
          data-line-key="${this.escapeHtml(data.key)}"
          aria-label="Remove ${this.escapeHtml(data.title)}"
        >\xD7</button>
        ${
          canSplit
            ? `<div class="multi-order-split-panel" data-split-panel="${this.escapeHtml(data.key)}" hidden>
          <div>
            <label>
              Quantity to move
              <input type="number" min="1" max="${data.quantity - 1}" value="1" data-split-quantity>
            </label>
            <label>
              Move to
              <select data-split-target>${splitTargets}</select>
            </label>
          </div>
          <p>Keep at least one unit in ${this.escapeHtml(this.groups.get(groupId)?.name || this.groupName(groupId))}.</p>
          <div class="multi-order-split-panel__actions">
            <button type="button" class="button" data-order-action="split-apply" data-line-key="${this.escapeHtml(data.key)}">Apply split</button>
            <button type="button" class="button outline" data-order-action="split-cancel" data-line-key="${this.escapeHtml(data.key)}">Cancel</button>
          </div>
        </div>`
            : ""
        }
      </article>`;
  }
  renderSummary(groups) {
    const orderCount = groups.length,
      orderText = `${orderCount} ${orderCount === 1 ? "Order" : "Orders"}`;
    (this.container
      .querySelector("[data-order-summary-count]")
      ?.replaceChildren(document.createTextNode(orderText)),
      this.container
        .querySelector("[data-order-summary-total-label]")
        ?.replaceChildren(document.createTextNode(orderText)));
    const lines = this.getLines(),
      totalCents = lines.reduce(
        (sum, line) => sum + this.lineData(line).lineTotal,
        0,
      ),
      totalElement = this.container.querySelector("[data-order-summary-total]");
    totalElement && (totalElement.textContent = this.formatMoney(totalCents));
    const preview = this.container.querySelector("[data-order-preview-rows]");
    if (preview) {
      const rows = groups
        .map(([id, group]) => {
          const groupLines = this.linesForGroup(id),
            itemCount = groupLines.reduce(
              (sum, line) => sum + this.lineData(line).quantity,
              0,
            ),
            groupTotal = groupLines.reduce(
              (sum, line) => sum + this.lineData(line).lineTotal,
              0,
            );
          return `<div class="multi-order-preview__row">
          <span>${this.escapeHtml(group.name)}${group.date ? ` (${this.escapeHtml(this.formatDate(group.date))})` : ""}</span>
          <span>${itemCount} ${itemCount === 1 ? "Item" : "Items"}</span>
          <strong>${this.formatMoney(groupTotal)}</strong>
        </div>`;
        })
        .join("");
      preview.innerHTML = `${rows}<div class="multi-order-preview__row multi-order-preview__total">
        <strong>Total</strong>
        <strong>${lines.reduce((sum, line) => sum + this.lineData(line).quantity, 0)} Items</strong>
        <strong>${this.formatMoney(totalCents)}</strong>
      </div>`;
    }
    const help = this.container.querySelector("[data-order-help]"),
      previewBox = this.container.querySelector("[data-order-preview]");
    (help && (help.hidden = this.mode !== "multiple"),
      previewBox && (previewBox.hidden = this.mode !== "multiple"));
  }
  itemCountText(lines) {
    const quantity = lines.reduce(
      (sum, line) => sum + this.lineData(line).quantity,
      0,
    );
    return `${quantity} ${quantity === 1 ? "item" : "items"}`;
  }
  lineData(line) {
    return {
      key: line.dataset.lineKey || "",
      title: line.dataset.lineTitle || "Product",
      url: line.dataset.lineUrl || "#",
      image: line.dataset.lineImage || "",
      sku: line.dataset.lineSku || "",
      unit: line.dataset.lineUnit || "EA",
      unitPrice: Number(line.dataset.lineUnitPrice || 0),
      lineTotal: Number(line.dataset.lineTotal || 0),
      inventory: Number(line.dataset.lineInventory || 0),
      quantity: Number(line.dataset.lineQuantity || 0),
      variantId: String(line.dataset.variantId || ""),
    };
  }
  toggleSplitPanel(lineKey, open) {
    const panel = Array.from(
      this.container.querySelectorAll("[data-split-panel]"),
    ).find((element) => element.dataset.splitPanel === lineKey);
    panel &&
      ((panel.hidden = !open),
      open && panel.querySelector("[data-split-quantity]")?.focus());
  }
  changeRenderedQuantity(lineKey, delta) {
    const line = this.findLine(lineKey);
    if (!line) return;
    const nextQuantity = Math.max(
      0,
      Number(line.dataset.lineQuantity || 0) + delta,
    );
    this.updateLineQuantity(lineKey, nextQuantity);
  }
  setRenderedQuantity(lineKey, value) {
    const quantity = Number(value);
    if (!Number.isInteger(quantity) || quantity < 1) {
      this.setStatus("Enter a whole-number quantity of 1 or more.", !0);
      this.render();
      return;
    }
    const line = this.findLine(lineKey);
    if (!line || quantity === Number(line.dataset.lineQuantity || 0)) return;
    this.updateLineQuantity(lineKey, quantity);
  }
  async updateLineQuantity(lineKey, quantity) {
    const line = this.findLine(lineKey);
    if (!line || this.busy) return;
    const source = this.sources.get(line.dataset.sourceId),
      allocation = this.allocations.find(
        (item) =>
          item.sourceId === line.dataset.sourceId &&
          item.groupId === (line.dataset.orderGroupId || "order-1"),
      );
    if (!source || !allocation) return;
    const previousAllocations = this.allocations.map((item) => ({ ...item })),
      nextSourceTotal =
        this.allocations
          .filter((item) => item.sourceId === allocation.sourceId)
          .reduce((sum, item) => sum + item.quantity, 0) -
        allocation.quantity +
        quantity;
    this.setBusy(!0);
    try {
      (await this.changeLine({
        id: source.sourceKey,
        quantity: nextSourceTotal,
      }),
        (allocation.quantity = quantity),
        (this.allocations = this.mergeAllocations(this.allocations)),
        this.rebuildVirtualLines(),
        this.persistState(),
        this.render(),
        this.setStatus(
          quantity === 0
            ? "Product removed from the cart."
            : "Quantity updated.",
        ),
        this.setBusy(!1),
        this.refreshCart());
    } catch (error) {
      ((this.allocations = previousAllocations),
        this.rebuildVirtualLines(),
        this.setStatus(error.message || "Could not update the quantity.", !0),
        this.setBusy(!1));
    }
  }
  moveWholeLine(lineKey, groupId) {
    const line = this.findLine(lineKey);
    if (!line || !this.groups.has(groupId) || this.busy) return;
    const previousGroup = line.dataset.orderGroupId || "order-1";
    if (groupId === previousGroup) return;
    const allocation = this.allocations.find(
      (item) =>
        item.sourceId === line.dataset.sourceId &&
        item.groupId === previousGroup,
    );
    allocation &&
      ((allocation.groupId = groupId),
      (this.allocations = this.mergeAllocations(this.allocations)),
      this.rebuildVirtualLines(),
      this.persistState(),
      this.render(),
      this.setStatus(
        `${line.dataset.lineTitle || "Product"} moved to ${this.groups.get(groupId).name}.`,
      ));
  }
  chooseDropAction(lineKey, groupId) {
    const line = this.findLine(lineKey),
      sourceGroupId = line?.dataset.orderGroupId || "order-1";
    if (
      !line ||
      !this.groups.has(groupId) ||
      groupId === sourceGroupId ||
      this.busy
    )
      return;
    const sourceName =
        this.groups.get(sourceGroupId)?.name || this.groupName(sourceGroupId),
      targetName = this.groups.get(groupId)?.name || this.groupName(groupId),
      quantity = Number(line.dataset.lineQuantity || 0),
      dialog = document.createElement("dialog");
    dialog.className = "multi-order-drop-dialog";
    dialog.setAttribute("aria-labelledby", "multi-order-drop-dialog-title");
    dialog.innerHTML = `
      <form method="dialog">
        <button type="submit" class="multi-order-drop-dialog__close" value="cancel" aria-label="Close">&times;</button>
        <span class="multi-order-drop-dialog__eyebrow">${this.escapeHtml(sourceName)} &rarr; ${this.escapeHtml(targetName)}</span>
        <h2 id="multi-order-drop-dialog-title">Move or copy this product?</h2>
        <p class="multi-order-drop-dialog__product">
          <strong>${this.escapeHtml(line.dataset.lineTitle || "This product")}</strong>
          <span>Quantity: ${quantity}</span>
        </p>
        <p class="multi-order-drop-dialog__prompt">Choose how this product should appear in ${this.escapeHtml(targetName)}.</p>
        <div class="multi-order-drop-dialog__actions">
          <button type="submit" class="multi-order-drop-dialog__choice" value="move">
            <span>Move product</span>
            <small>Remove it from ${this.escapeHtml(sourceName)} and place it in ${this.escapeHtml(targetName)}.</small>
          </button>
          <button type="submit" class="multi-order-drop-dialog__choice multi-order-drop-dialog__choice--primary" value="copy">
            <span>Add to both orders</span>
            <small>Keep it in ${this.escapeHtml(sourceName)} and add the same quantity to ${this.escapeHtml(targetName)}.</small>
          </button>
        </div>
        <button type="submit" class="multi-order-drop-dialog__cancel" value="cancel">Cancel</button>
      </form>`;
    dialog.addEventListener(
      "close",
      () => {
        const action = dialog.returnValue;
        dialog.remove();
        action === "move"
          ? this.moveWholeLine(lineKey, groupId)
          : action === "copy"
            ? this.copyWholeLine(lineKey, groupId)
            : this.render();
      },
      { once: !0 },
    );
    document.body.appendChild(dialog);
    dialog.showModal();
  }
  async copyWholeLine(lineKey, groupId) {
    const line = this.findLine(lineKey);
    if (!line || !this.groups.has(groupId) || this.busy) return;
    const sourceGroupId = line.dataset.orderGroupId || "order-1";
    if (groupId === sourceGroupId) return;
    const source = this.sources.get(line.dataset.sourceId),
      quantity = Number(line.dataset.lineQuantity || 0);
    if (!source || !Number.isInteger(quantity) || quantity < 1) return;
    const previousAllocations = this.allocations.map((item) => ({ ...item })),
      currentSourceTotal = this.allocations
        .filter((item) => item.sourceId === line.dataset.sourceId)
        .reduce((sum, item) => sum + item.quantity, 0);
    this.setBusy(!0);
    try {
      (await this.changeLine({
        id: source.sourceKey,
        quantity: currentSourceTotal + quantity,
      }),
        this.allocations.push({
          sourceId: line.dataset.sourceId,
          groupId,
          quantity,
        }),
        (this.allocations = this.mergeAllocations(this.allocations)),
        this.rebuildVirtualLines(),
        this.persistState(),
        this.render(),
        this.setStatus(
          `${line.dataset.lineTitle || "Product"} was added to ${this.groups.get(groupId).name} and remains in ${this.groups.get(sourceGroupId)?.name || this.groupName(sourceGroupId)}.`,
        ),
        this.setBusy(!1),
        this.refreshCart());
    } catch (error) {
      ((this.allocations = previousAllocations),
        this.rebuildVirtualLines(),
        this.setStatus(
          error.message || "Could not add the product to both orders.",
          !0,
        ),
        this.setBusy(!1));
    }
  }
  splitRenderedLine(lineKey) {
    const line = this.findLine(lineKey),
      panel = Array.from(
        this.container.querySelectorAll("[data-split-panel]"),
      ).find((element) => element.dataset.splitPanel === lineKey);
    if (!line || !panel || this.busy) return;
    const quantity = Number(
        panel.querySelector("[data-split-quantity]")?.value,
      ),
      targetGroup = panel.querySelector("[data-split-target]")?.value,
      originalQuantity = Number(line.dataset.lineQuantity);
    if (
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity >= originalQuantity
    ) {
      this.setStatus(
        `Enter a quantity between 1 and ${originalQuantity - 1}.`,
        !0,
      );
      return;
    }
    if (!targetGroup || !this.groups.has(targetGroup)) {
      this.setStatus("Choose an order for the split quantity.", !0);
      return;
    }
    const sourceAllocation = this.allocations.find(
      (item) =>
        item.sourceId === line.dataset.sourceId &&
        item.groupId === (line.dataset.orderGroupId || "order-1"),
    );
    sourceAllocation &&
      ((sourceAllocation.quantity -= quantity),
      this.allocations.push({
        sourceId: sourceAllocation.sourceId,
        groupId: targetGroup,
        quantity,
      }),
      (this.allocations = this.mergeAllocations(this.allocations)),
      this.rebuildVirtualLines(),
      this.persistState(),
      this.render(),
      this.setStatus(
        `${quantity} ${quantity === 1 ? "unit" : "units"} moved to ${this.groups.get(targetGroup).name}.`,
      ));
  }
  setGroupDate(groupId, date) {
    if (!this.groups.has(groupId) || this.busy) return;
    const group = this.groups.get(groupId);
    group.date = date;
    const error = this.deliveryDateError(groupId, group);
    (error
      ? this.deliveryDateErrors.set(groupId, error)
      : this.deliveryDateErrors.delete(groupId),
      this.persistState(),
      this.render(),
      this.setStatus(error || `${group.name} delivery date saved.`, !!error));
  }
  poScope(scopeId) {
    return scopeId ? this.groups.get(scopeId) : this.sharedPo;
  }
  poNumberOf(scope) {
    return String((scope === this.sharedPo ? scope.number : scope?.po) || "");
  }
  poScopeName(scopeId) {
    return scopeId ? this.groups.get(scopeId)?.name || scopeId : "All orders";
  }
  isSeparatePo() {
    return this.poMode === "separate" && this.mode === "multiple";
  }
  effectivePo(groupId) {
    const scope = this.isSeparatePo() ? this.groups.get(groupId) : this.sharedPo;
    return {
      number: this.poNumberOf(scope),
      fileUrl: String(scope?.fileUrl || scope?.poFileUrl || ""),
      fileName: String(scope?.fileName || scope?.poFileName || ""),
      attachments: scope?.attachments || {},
    };
  }
  poBadgeText(groupId) {
    if (!this.isSeparatePo()) return "Shared PO";
    const po = this.groups.get(groupId)?.po || "";
    return po ? `PO ${po}` : "No PO yet";
  }
  setPoMode(poMode) {
    if (!["shared", "separate"].includes(poMode) || poMode === this.poMode) return;
    ((this.poMode = poMode),
      this.persistState(),
      this.render(),
      this.setStatus(
        poMode === "shared"
          ? "One PO will be applied to every order."
          : "Each order now takes its own PO number and document.",
      ));
  }
  // No re-render: a full rebuild here would steal focus mid-typing.
  bufferPoNumber(scopeId, value) {
    const scope = this.poScope(scopeId);
    if (!scope) return;
    const po = String(value || "").slice(0, 50);
    scope === this.sharedPo ? (scope.number = po) : (scope.po = po);
  }
  setPoNumber(scopeId, value) {
    const scope = this.poScope(scopeId);
    if (!scope) return;
    const po = String(value || "")
      .trim()
      .slice(0, 50);
    if (po === this.poNumberOf(scope)) return;
    (scope === this.sharedPo ? (scope.number = po) : (scope.po = po),
      this.persistState(),
      this.refreshPoBadges(),
      this.setStatus(
        po
          ? `PO number saved for ${this.poScopeName(scopeId)}.`
          : `PO number cleared for ${this.poScopeName(scopeId)}.`,
      ));
  }
  refreshPoBadges() {
    this.container
      .querySelectorAll("[data-order-drop-zone] .multi-order-po-badge")
      .forEach((badge) => {
        const groupId = badge.closest("[data-order-drop-zone]")?.dataset.groupId;
        groupId && (badge.textContent = this.poBadgeText(groupId));
      });
  }
  setPoDocument(scopeId, fileUrl, fileName, documentKey = "po") {
    const scope = this.poScope(scopeId);
    if (!scope || !["po", "doc1", "doc2"].includes(documentKey)) return;
    if (documentKey !== "po") {
      scope.attachments = { ...scope.attachments, [documentKey]: { fileUrl, fileName } };
    } else if (scope === this.sharedPo) {
      scope.fileUrl = fileUrl;
      scope.fileName = fileName;
    } else {
      scope.poFileUrl = fileUrl;
      scope.poFileName = fileName;
    }
    this.persistState();
  }
  poUploadKey(scopeId, documentKey = "po") {
    const scope = scopeId || "shared";
    return documentKey === "po" ? scope : `${scope}:${documentKey}`;
  }
  async uploadPoDocument(scopeId, file, documentKey = "po") {
    if (!file || this.busy || !this.poScope(scopeId) || !["po", "doc1", "doc2"].includes(documentKey)) return;
    const uploadKey = this.poUploadKey(scopeId, documentKey);
    // Each field can upload independently, without replacing an in-flight file.
    if (this.poUploads.has(uploadKey)) {
      this.setStatus(
        `${this.poScopeName(scopeId)} is still uploading ${documentKey === "po" ? "a PO document" : documentKey === "doc1" ? "Doc1" : "Doc2"}. Please wait for it to finish.`,
        !0,
      );
      return;
    }
    const baseUrl = window.AGTHIA?.appBaseUrl,
      shopDomain = window.AGTHIA?.shopDomain;
    if (!baseUrl || !shopDomain) {
      this.setStatus("Document upload is unavailable right now.", !0);
      return;
    }
    (this.poUploads.add(uploadKey),
      this.renderPoPanel(),
      this.updateCheckoutAvailability(),
      this.setStatus(`Uploading ${file.name}.`));
    try {
      const form = new FormData();
      form.append("poDocument", file);
      const response = await fetch(
          `${baseUrl}/storefront/${shopDomain}/po-document-upload`,
          { method: "POST", body: form },
        ),
        body = await response.json();
      if (!response.ok || !body?.fileUrl)
        throw new Error(body?.error || "Could not upload the document.");
      (this.setPoDocument(scopeId, body.fileUrl, body.filename || file.name, documentKey),
        this.setStatus(
          `${body.filename || file.name} attached to ${this.poScopeName(scopeId)}.`,
        ));
    } catch (error) {
      this.setStatus(
        error.message || "Could not upload the document.",
        !0,
      );
    } finally {
      (this.poUploads.delete(uploadKey),
        this.renderPoPanel(),
        this.updateCheckoutAvailability());
    }
  }
  removePoDocument(scopeId, documentKey = "po") {
    if (this.busy || this.poUploads.has(this.poUploadKey(scopeId, documentKey))) return;
    (this.setPoDocument(scopeId, "", "", documentKey),
      this.renderPoPanel(),
      this.setStatus(`${documentKey === "po" ? "PO document" : documentKey === "doc1" ? "Doc1" : "Doc2"} removed from ${this.poScopeName(scopeId)}.`));
  }
  documentFieldMarkup(scopeId, documentKey, label) {
    const scope = this.poScope(scopeId),
      document = documentKey === "po"
        ? {
            fileUrl: scope === this.sharedPo ? scope.fileUrl : scope?.poFileUrl,
            fileName: scope === this.sharedPo ? scope.fileName : scope?.poFileName,
          }
        : scope?.attachments?.[documentKey] || {},
      uploading = this.poUploads.has(this.poUploadKey(scopeId, documentKey)),
      escapedScope = this.escapeHtml(scopeId);
    return `
      <div class="multi-order-po-field">
        <span class="multi-order-po-field__label">${label}</span>
        <div class="multi-order-po-upload">
          <label class="multi-order-po-upload__button${uploading ? " is-busy" : ""}">
            <span aria-hidden="true">\u21A5</span>
            <span>${uploading ? "Uploading\u2026" : "Upload file"}</span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              ${uploading || this.busy ? "disabled" : ""}
              data-order-po-file
              data-document-key="${documentKey}"
              data-scope-id="${escapedScope}"
              aria-label="${label}${documentKey === "po" ? "" : " (optional)"} for ${this.escapeHtml(this.poScopeName(scopeId))}"
            >
          </label>
          <span class="multi-order-po-upload__hint">PDF, DOC, JPG or PNG</span>
        </div>
        ${document.fileUrl ? `<p class="multi-order-po-file">
          <a href="${this.escapeHtml(document.fileUrl)}" target="_blank" rel="noopener">${this.escapeHtml(document.fileName || label)}</a>
          <button
            type="button"
            class="multi-order-po-file__remove"
            data-order-action="remove-po-document"
            data-document-key="${documentKey}"
            data-scope-id="${escapedScope}"
            aria-label="Remove ${label} for ${this.escapeHtml(this.poScopeName(scopeId))}"
          >Remove</button>
        </p>` : ""}
      </div>`;
  }
  poFieldsMarkup(scopeId, heading) {
    const po = scopeId
        ? {
            number: this.groups.get(scopeId)?.po || "",
            fileUrl: this.groups.get(scopeId)?.poFileUrl || "",
            fileName: this.groups.get(scopeId)?.poFileName || "",
          }
        : { ...this.sharedPo },
      escapedScope = this.escapeHtml(scopeId);
    return `
      <div class="multi-order-po-fields" data-po-scope="${escapedScope}">
        ${heading ? `<h6 class="multi-order-po-fields__heading">${this.escapeHtml(heading)}</h6>` : ""}
        <label class="multi-order-po-field">
          <span class="multi-order-po-field__label">PO Number</span>
          <input
            type="text"
            value="${this.escapeHtml(po.number)}"
            maxlength="50"
            autocomplete="off"
            placeholder="Enter PO number"
            data-order-po
            data-scope-id="${escapedScope}"
            aria-label="PO number for ${this.escapeHtml(this.poScopeName(scopeId))}"
          >
        </label>
        ${this.documentFieldMarkup(scopeId, "po", "Attach PO")}
        <section class="multi-order-other-attachments" aria-label="Other optional attachments for ${this.escapeHtml(this.poScopeName(scopeId))}">
          <div class="multi-order-other-attachments__heading">
            <h6>Other Attachments</h6>
            <span class="multi-order-other-attachments__optional">Optional</span>
          </div>
          ${this.documentFieldMarkup(scopeId, "doc1", "Doc1")}
          ${this.documentFieldMarkup(scopeId, "doc2", "Doc2")}
          <p class="multi-order-po-note multi-order-po-note--muted">Both documents are optional.</p>
        </section>
      </div>`;
  }
  renderPoPanel() {
    const panel = this.container.querySelector("[data-order-po-panel]");
    if (!panel) return;
    const activeGroups = this.sortedGroups().filter(
        ([groupId]) => this.linesForGroup(groupId).length > 0,
      ),
      appliesTo = (activeGroups.length ? activeGroups : this.sortedGroups())
        .map(([, group]) => group.name)
        .join(", ")
        .replace(/, ([^,]*)$/, " and $1");
    panel.innerHTML = `
      <h5>Purchase Order Details</h5>
      ${
        this.mode === "multiple"
          ? `<fieldset class="multi-order-po-mode">
        <legend>How should the PO be applied?</legend>
        <label>
          <input type="radio" name="agthia-po-mode" value="shared" data-order-po-mode
            ${this.poMode === "shared" ? "checked" : ""}>
          <span>One PO for all orders</span>
        </label>
        <label>
          <input type="radio" name="agthia-po-mode" value="separate" data-order-po-mode
            ${this.poMode === "separate" ? "checked" : ""}>
          <span>Separate PO for each order</span>
        </label>
      </fieldset>`
          : ""
      }
      ${
        this.isSeparatePo()
          ? this.sortedGroups()
              .map(([groupId, group]) =>
                this.poFieldsMarkup(groupId, group.name),
              )
              .join("")
          : this.poFieldsMarkup("", "")
      }
      ${
        this.mode === "multiple" && !this.isSeparatePo() && appliesTo
          ? `<p class="multi-order-po-note">This PO and any other attachments will be applied to ${this.escapeHtml(appliesTo)}.</p>`
          : ""
      }
      <p class="multi-order-po-note multi-order-po-note--muted">
        Optional. A PO entered here overrides anything typed at checkout.
      </p>`;
  }
  estimatedDeliveryDate() {
    return this.minimumDeliveryDate;
  }
  calculateEstimatedDeliveryDate() {
    const date = new Date();
    return (date.setDate(date.getDate() + 2), this.toIsoDate(date));
  }
  toIsoDate(date) {
    const year = date.getFullYear(),
      month = String(date.getMonth() + 1).padStart(2, "0"),
      day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  deliveryDateError(groupId, group = this.groups.get(groupId)) {
    return !group || this.linesForGroup(groupId).length === 0
      ? ""
      : group.date
        ? !/^\d{4}-\d{2}-\d{2}$/.test(group.date) ||
          group.date < this.estimatedDeliveryDate()
          ? `${group.name} requested delivery date must be on or after ${this.formatDate(this.estimatedDeliveryDate())}.`
          : ""
        : `Please choose a requested delivery date for ${group.name}.`;
  }
  validateDeliveryDates() {
    this.deliveryDateErrors.clear();
    let firstInvalidGroupId = "";
    return (
      this.sortedGroups().forEach(([groupId, group]) => {
        const error = this.deliveryDateError(groupId, group);
        error &&
          (this.deliveryDateErrors.set(groupId, error),
          firstInvalidGroupId || (firstInvalidGroupId = groupId));
      }),
      firstInvalidGroupId
    );
  }
  focusDeliveryDate(groupId) {
    window.requestAnimationFrame(() => {
      const input = Array.from(
        this.container.querySelectorAll("[data-order-delivery-date]"),
      ).find((element) => element.dataset.groupId === groupId);
      (input?.scrollIntoView({ behavior: "smooth", block: "center" }),
        input?.focus({ preventScroll: !0 }));
    });
  }
  buildCheckoutPlan() {
    return {
      v: 1,
      g: this.sortedGroups().flatMap(([groupId, group]) => {
        const lines = this.linesForGroup(groupId);
        return lines.length
          ? [
              {
                i: groupId,
                n: group.name,
                d: group.date || "",
                p: this.effectivePo(groupId).number,
                f: this.effectivePo(groupId).fileUrl,
                d1: this.effectivePo(groupId).attachments.doc1?.fileUrl || "",
                d2: this.effectivePo(groupId).attachments.doc2?.fileUrl || "",
                l: lines.map((line) => {
                  const data = this.lineData(line);
                  return {
                    v: String(data.variantId),
                    s: data.sku,
                    q: data.quantity,
                  };
                }),
              },
            ]
          : [];
      }),
    };
  }
  multiOrderPlanReady() {
    if (this.mode !== "multiple") return true;
    const plan = this.buildCheckoutPlan(),
      cartQuantity = Array.from(this.sources.values()).reduce(
        (sum, source) => sum + Number(source.quantity || 0),
        0,
      ),
      plannedQuantity = plan.g.reduce(
        (sum, group) =>
          sum +
          group.l.reduce((lineSum, line) => lineSum + Number(line.q || 0), 0),
        0,
      );
    return (
      plan.g.length >= 2 &&
      cartQuantity > 0 &&
      plannedQuantity === cartQuantity
    );
  }
  updateCheckoutAvailability() {
    const splitMissing =
        this.mode === "multiple" && !this.multiOrderPlanReady(),
      disabled =
        this.busy || this.checkoutSubmitting || splitMissing || this.poUploads.size > 0,
      reason = this.poUploads.size
        ? `Please wait while your ${this.poUploads.size === 1 ? "document" : "documents"} finish${this.poUploads.size === 1 ? "es" : ""} uploading.`
        : this.busy || this.checkoutSubmitting
          ? "Please wait while your cart is being saved."
          : splitMissing
            ? "Add products to at least two orders before checkout."
            : "";
    this.container
      .querySelectorAll('button[name="checkout"], input[name="checkout"]')
      .forEach((button) => {
        button.disabled = disabled;
        button.setAttribute("aria-disabled", String(disabled));
        reason ? button.setAttribute("title", reason) : button.removeAttribute("title");
      });
  }
  checkoutAttributes(cartAttributes = {}) {
    const plan = this.mode === "multiple" ? this.buildCheckoutPlan() : null,
      chunkSize = 240,
      chunks = [];
    if (plan) {
      const cartQuantity = Array.from(this.sources.values()).reduce(
          (sum, source) => sum + Number(source.quantity || 0),
          0,
        ),
        plannedQuantity = plan.g.reduce(
          (sum, group) =>
            sum +
            group.l.reduce((lineSum, line) => lineSum + Number(line.q || 0), 0),
          0,
        );
      if (plan.g.length < 2)
        throw new Error(
          "Multiple Orders needs products in at least two orders. Please review your order groups.",
        );
      if (!cartQuantity || plannedQuantity !== cartQuantity)
        throw new Error(
          "Your multi-order plan is still updating. Please wait a moment and try checkout again.",
        );
      const serialized = JSON.stringify(plan);
      for (let offset = 0; offset < serialized.length; offset += chunkSize)
        chunks.push(serialized.slice(offset, offset + chunkSize));
    }
    if (chunks.length > 200)
      throw new Error(
        "The multi-order plan is too large to attach to checkout.",
      );
    const attributes = {
        __agthiaOrderPlanParts: chunks.length ? String(chunks.length) : "",
      },
      activeGroups = this.sortedGroups().filter(
        ([groupId]) => this.linesForGroup(groupId).length > 0,
      ),
      primaryGroup =
        activeGroups.find(([groupId]) => groupId === "order-1") ||
        activeGroups[0];
    const primaryPo = this.effectivePo(primaryGroup?.[0] || "order-1");
    ((attributes["Requested Delivery Date"] = primaryGroup?.[1]?.date || ""),
      (attributes.__agthiaOrderPo = primaryPo.number),
      (attributes["PO Document"] = primaryPo.fileUrl),
      (attributes.Doc1 = primaryPo.attachments.doc1?.fileUrl || ""),
      (attributes.Doc2 = primaryPo.attachments.doc2?.fileUrl || ""),
      chunks.forEach((chunk, index) => {
        attributes[`__agthiaOrderPlan${index + 1}`] = chunk;
      }));
    Object.keys(cartAttributes).forEach((key) => {
      if (/^__agthiaOrderPlan\d+$/.test(key) && !(key in attributes))
        attributes[key] = "";
    });
    return attributes;
  }
  async startCheckout(form) {
    if (this.busy || this.checkoutSubmitting) return;
    if (this.poUploads.size) {
      this.setStatus("Please wait for your documents to finish uploading.", !0);
      return;
    }
    this.syncAllocationsFromCart();
    const firstInvalidGroupId = this.validateDeliveryDates();
    if (firstInvalidGroupId) {
      const message = this.deliveryDateErrors.get(firstInvalidGroupId);
      (this.render(),
        this.setStatus(
          message || "Please choose a valid requested delivery date.",
          !0,
        ),
        this.focusDeliveryDate(firstInvalidGroupId));
      return;
    }
    (this.setBusy(!0),
      this.setStatus("Preparing your order plan for checkout."));
    try {
      (await this.updateCartAttributes(this.checkoutAttributes(
        JSON.parse(this.builder().dataset.cartAttributes || "{}"),
      )),
        this.persistState(),
        (this.checkoutSubmitting = !0));
      let checkoutInput = form.querySelector(
        "input[data-agthia-checkout-input]",
      );
      (checkoutInput ||
        ((checkoutInput = document.createElement("input")),
        (checkoutInput.type = "hidden"),
        (checkoutInput.name = "checkout"),
        (checkoutInput.value = "Checkout"),
        (checkoutInput.dataset.agthiaCheckoutInput = "true"),
        form.appendChild(checkoutInput)),
        this.clearSavedState(),
        HTMLFormElement.prototype.submit.call(form));
    } catch (error) {
      (this.setStatus(
        error.message || "Could not prepare checkout. Please try again.",
        !0,
      ),
        this.setBusy(!1));
    }
  }
  async changeLine(input) {
    const response = await fetch(theme.routes.cart_change_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        agthiaSkipSapCartHooks: !0,
        body: JSON.stringify(input),
      }),
      body = await response.json();
    if (!response.ok || body.errors)
      throw new Error(
        body.description ||
          body.message ||
          body.errors ||
          "Shopify could not update the cart.",
      );
    return body;
  }
  async updateCartAttributes(attributes) {
    const response = await fetch(theme.routes.cart_update_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        agthiaSkipSapCartHooks: !0,
        body: JSON.stringify({ attributes }),
      }),
      body = await response.json();
    if (!response.ok || body.errors)
      throw new Error(
        body.description ||
          body.message ||
          body.errors ||
          "Shopify could not save the order date.",
      );
    return body;
  }
  refreshCart() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }
  setBusy(isBusy) {
    ((this.busy = isBusy),
      this.container.classList.toggle("multi-order-is-busy", isBusy),
      this.container
        .querySelectorAll(
          "[data-order-splitter] button, [data-cart-order-splitter] button, [data-cart-order-splitter] select, [data-cart-order-splitter] input, [data-order-mode]",
        )
        .forEach((element) => {
          element.disabled = isBusy;
        }),
      this.renderModeControls(),
      this.updateCheckoutAvailability());
  }
  setStatus(message, isError = !1) {
    ((this.lastStatus = { message, isError }),
      this.renderStatus(message, isError));
  }
  renderStatus(message, isError) {
    const status = this.container.querySelector("[data-order-split-status]");
    status &&
      ((status.hidden = !1),
      (status.textContent = message),
      status.classList.toggle("multi-order-status--error", isError));
  }
  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || "AED";
    return `${(Number(cents || 0) / 100).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  }
  formatDate(value) {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : date
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          .replace(/ /g, "-");
  }
  escapeHtml(value) {
    const element = document.createElement("span");
    return ((element.textContent = String(value ?? "")), element.innerHTML);
  }
}
window.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("Cart");
  container?.querySelector("[data-cart-order-splitter]") &&
    new CartOrderSplitter(container);
});
//# sourceMappingURL=/cdn/shop/t/2/assets/cart-order-splitter.js.map
